import type { CurrentUser } from '@app/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { getCurrentUser, updateProfile } from '../api/user'
import { logout as apiLogout } from '../api/auth'
import { profileLocaleOf, useAdoptProfileLocale, useLocale } from '../i18n'
import { isAuthenticated as hasSession, onSessionChange } from '../lib/session'

interface SessionValue {
  /** Null while loading, and whenever there is no session. */
  user: CurrentUser | null
  /** A refresh token exists. The profile may still be loading. */
  authenticated: boolean
  loading: boolean
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient()
  const [authenticated, setAuthenticated] = useState(hasSession)

  // Covers signing out in another tab as well as in this one.
  useEffect(() => onSessionChange(setAuthenticated), [])

  const query = useQuery({
    queryKey: ['session'],
    queryFn: getCurrentUser,
    enabled: authenticated,
    staleTime: 60_000,
    retry: false,
  })

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['session'] })
  }, [queryClient])

  // The profile language words every e-mail and push, and the two sides are
  // reconciled once per sign-in, when the profile loads:
  //
  //  - a device on which no language was ever picked follows the profile, so
  //    a choice made on the phone reaches the laptop instead of the laptop
  //    starting in the browser's language while the letters keep arriving in
  //    the chosen one;
  //  - a device with a choice of its own is what the person is actually
  //    reading, so the profile is brought in line with the screen (an account
  //    created from the English interface before sign-up carried the language
  //    kept Russian letters coming until the language screen was visited); the
  //    same when the profile names a language the app does not ship.
  const { locale } = useLocale()
  const adoptProfileLocale = useAdoptProfileLocale()
  const languageSyncedFor = useRef<string | null>(null)
  useEffect(() => {
    const current = query.data
    if (current === undefined || current.profile === null) return
    if (languageSyncedFor.current === current.user.id) return
    languageSyncedFor.current = current.user.id
    if (adoptProfileLocale(current.profile.language)) return
    if (profileLocaleOf(current.profile.language) === locale) return
    void updateProfile({ language: locale })
      .then((updated) => {
        queryClient.setQueryData(['session'], updated)
      })
      .catch(() => undefined)
  }, [query.data, locale, queryClient, adoptProfileLocale])

  const signOut = useCallback(async (): Promise<void> => {
    await apiLogout()
    // Balances and orders must not survive into the next session.
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<SessionValue>(
    () => ({
      user: query.data ?? null,
      authenticated,
      loading: authenticated && query.isLoading,
      refresh,
      signOut,
    }),
    [authenticated, query.data, query.isLoading, refresh, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionProvider>')
  return value
}
